// ─────────────────────────────────────────────
//  LightChart — the day's cases, read-only.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Same geometry as the full Timeline Chart — `buildTimeline`'s model, the same
//  `barMetrics`, the same section tints and shift-label colours — so a CRNA and
//  the AIC are looking at ONE picture of the day. What is missing is every
//  write: no click-to-edit, no relieve, no time editing. A bar opens a detail
//  card and nothing else.
//
//  ⚠️ DO NOT "improve" this by importing TimelineChartPage's editing pieces.
//  The whole reason this route exists is that a hidden control is one bug from
//  reachable; there is nothing here to hide.
//
//  What it adds over the manager's chart, because the reader is different:
//    • YOUR rooms are marked and can be isolated — a CRNA's first question is
//      "where am I", not "where is everyone".
//    • A NOW line, because this is read during the day, not planned the night
//      before.
//    • Tapping a bar tells you the things you'd otherwise ask someone: who is
//      supervising, who is operating, when the case ends, when they leave.
// ─────────────────────────────────────────────
import React, { useMemo, useRef, useState } from "react";
import {
  barMetrics, to12h, shiftLabelColor, shiftHatchPercent,
} from "../../domain/timeline";
import tokens from "../../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// Minutes since midnight, right now. Passed in rather than read here so the
// page owns the tick and the whole screen agrees on "now".
export function nowLeftPercent(nowMin, axisStart, span) {
  if (!Number.isFinite(nowMin) || !span) return null;
  const pct = ((nowMin - axisStart) / span) * 100;
  return pct >= 0 && pct <= 100 ? pct : null;
}

// Label thinning: hour marks collide before they overlap, so drop every other
// one (or two) once they are packed tighter than ~56px.
export function labelStep(hourCount, widthPx) {
  if (!hourCount || !widthPx) return 1;
  const per = widthPx / hourCount;
  if (per >= 56) return 1;
  if (per >= 32) return 2;
  return 3;
}

export default function LightChart({ model, nowMin, myRoomIds, onPickCase, onlyMine }) {
  const { sections = [], axisStart, span, hourMarks = [] } = model || {};
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);

  // ⚠️ A CALLBACK REF, not an effect keyed on []. The chart mounts behind a
  // loading state, so an effect measuring on mount measures a node that is not
  // there yet and silently reports 0 forever.
  const measure = (node) => {
    trackRef.current = node;
    if (node) setTrackWidth(node.getBoundingClientRect().width);
  };

  const step = labelStep(hourMarks.length, trackWidth);
  const nowPct = nowLeftPercent(nowMin, axisStart, span);

  const visible = useMemo(() => {
    if (!onlyMine) return sections;
    return sections
      .map((sec) => ({ ...sec, rooms: sec.rooms.filter((r) => myRoomIds.has(r.id)) }))
      .filter((sec) => sec.rooms.length);
  }, [sections, onlyMine, myRoomIds]);

  if (!visible.length) {
    return (
      <div style={s.empty}>
        {onlyMine
          ? "You're not on a room today. Turn off \"Only my rooms\" to see the whole board."
          : "No cases on the board for this day yet."}
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      {/* HOUR AXIS */}
      <div style={s.axisRow}>
        <div style={s.gutter} aria-hidden="true" />
        <div style={s.axisTrack} ref={measure}>
          {hourMarks.map((h, i) => (
            <span
              key={h.min}
              style={{
                ...s.axisTick,
                left: `${barMetrics(h.min, h.min, axisStart, span).left}%`,
                // Hidden, not removed: the tick line still marks the hour even
                // where the label would collide with its neighbour.
                ...(i % step === 0 ? {} : { color: "transparent" }),
              }}
            >
              {to12h(h.min)}
            </span>
          ))}
        </div>
      </div>

      {visible.map((sec) => (
        <section key={sec.key} style={s.section}>
          <div style={s.sectionHeadRow}>
            <span
              style={{
                ...s.sectionHead,
                background: sec.tint.bg,
                color: sec.tint.head,
                borderColor: sec.tint.border,
              }}
            >
              {sec.label}
            </span>
            <span style={s.sectionRule} aria-hidden="true" />
          </div>

          {sec.rooms.map((room) => {
            const mine = myRoomIds.has(room.id);
            return (
              <div key={room.id} style={{ ...s.row, ...(mine ? s.rowMine : {}) }}>
                <div style={{ ...s.gutter, ...(mine ? s.gutterMine : {}) }}>
                  <span style={s.roomName}>{room.name}</span>
                  {mine && <span style={s.youChip}>YOU</span>}
                </div>

                <div style={s.track}>
                  {/* Hour gridlines, behind the bars. */}
                  {hourMarks.map((h) => (
                    <span
                      key={h.min}
                      aria-hidden="true"
                      style={{ ...s.grid, left: `${barMetrics(h.min, h.min, axisStart, span).left}%` }}
                    />
                  ))}

                  {room.cases.map((c) => {
                    const { left, width } = barMetrics(c.startMin, c.endMin, axisStart, span);
                    const hatch = shiftHatchPercent(c);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => onPickCase({ ...c, sectionLabel: sec.label })}
                        style={{
                          ...s.bar,
                          left: `${left}%`,
                          width: `${width}%`,
                          background: sec.tint.bg,
                          borderColor: sec.tint.border,
                          color: sec.tint.head,
                          ...(c.onCall ? s.barOnCall : {}),
                        }}
                        title={`${room.name} · ${c.start12h}–${c.end12h}\n${c.label}`}
                      >
                        {hatch > 0 && (
                          <span aria-hidden="true" style={{ ...s.barHatch, width: `${hatch}%` }} />
                        )}
                        <span style={{ ...s.barLabel, color: shiftLabelColor(c.depart12h) }}>
                          {c.label}
                          {c.depart12h && <span style={s.barDepart}> {c.depart12h}</span>}
                        </span>
                        {c.sub && <span style={s.barSub}>{c.sub}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      ))}

      {/* NOW — drawn last so it sits over every bar. Offset by the gutter so it
          lines up with the track, not the page. */}
      {nowPct != null && (
        <div style={s.nowWrap} aria-hidden="true">
          <div style={{ ...s.nowLine, left: `calc(${GUTTER}px + (100% - ${GUTTER}px) * ${nowPct / 100})` }}>
            <span style={s.nowFlag}>{to12h(nowMin)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const GUTTER = 92;
const ROW_H = 40;

const s = {
  wrap: { position: "relative", minWidth: 620 },
  axisRow: { display: "flex", alignItems: "flex-end", height: 26, marginBottom: space.xs },
  axisTrack: { position: "relative", flex: 1, height: "100%" },
  axisTick: {
    position: "absolute",
    bottom: 0,
    transform: "translateX(-50%)",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".6px",
    color: color.text.muted,
    whiteSpace: "nowrap",
  },

  section: { marginBottom: space.md },
  sectionHeadRow: { display: "flex", alignItems: "center", gap: space.sm, marginBottom: 6 },
  sectionHead: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: radius.full,
    border: "1px solid",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
  },
  // The home page's brass hairline, carried through as the section marker.
  sectionRule: { flex: 1, height: 1, background: lobby.brass, opacity: 0.5 },

  row: { display: "flex", alignItems: "center", height: ROW_H, borderRadius: radius.sm },
  rowMine: { background: "#1e40af0d" },
  gutter: {
    width: GUTTER,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
    paddingRight: space.sm,
    boxSizing: "border-box",
  },
  gutterMine: {},
  roomName: { fontSize: font.size.label, fontWeight: font.weight.bold, color: color.text.secondary, whiteSpace: "nowrap" },
  youChip: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".8px",
    color: "#fff",
    background: color.accent.primary,
    borderRadius: radius.full,
    padding: "1px 5px",
  },

  track: { position: "relative", flex: 1, height: ROW_H - 8, minWidth: 0 },
  grid: { position: "absolute", top: 0, bottom: 0, width: 1, background: color.border.default, opacity: 0.45 },

  bar: {
    position: "absolute",
    top: 0,
    bottom: 0,
    border: "1px solid",
    borderRadius: radius.sm,
    padding: "2px 6px",
    overflow: "hidden",
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 8,
  },
  barOnCall: { borderStyle: "dashed" },
  barHatch: {
    position: "absolute",
    left: 0,
    top: 0,
    height: "50%",
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(13,15,26,.18) 0 3px, transparent 3px 7px)",
    pointerEvents: "none",
  },
  barLabel: {
    fontSize: 11,
    fontWeight: font.weight.bold,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    position: "relative",
  },
  barDepart: { fontWeight: font.weight.semibold },
  barSub: {
    fontSize: 9,
    color: color.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    position: "relative",
  },

  nowWrap: { position: "absolute", inset: 0, pointerEvents: "none" },
  nowLine: { position: "absolute", top: 22, bottom: 0, width: 2, background: color.text.primary },
  nowFlag: {
    position: "absolute",
    top: -18,
    left: "50%",
    transform: "translateX(-50%)",
    background: color.text.primary,
    color: "#fff",
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".6px",
    borderRadius: radius.sm,
    padding: "2px 5px",
    whiteSpace: "nowrap",
  },

  empty: {
    padding: space.xl,
    textAlign: "center",
    color: color.text.secondary,
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
  },
};
